import React, { createRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { parseSqlDate } from 'ck-utils/dateTime';
import './FlipTimer.global.scss';

/**
 * jQuery wrapper to initialise the Flip countdown plugin
 * @param {string} location - The store location overrides
 * @param {Element} element - The container element
 */
const initFlipCountdown = (location, element) => {
  const finalDate = element.dataset.flipCountdown;
  const labels = ['days', 'hours', 'minutes', 'seconds'];
  let currDate = '00:00:00:00:00';
  let nextDate = '00:00:00:00:00';
  const parser = /([0-9]{2})/gi;

  const timezoneAwareDate = parseSqlDate(finalDate, location);

  // Parse countdown string to an object
  const strfobj = (str) => {
    const parsed = str.match(parser);
    const obj = {};
    labels.forEach((label, i) => {
      obj[label] = parsed[i];
    });
    return obj;
  };

  // Return the time components that diffs
  const diff = (obj1, obj2) => {
    const diffList = [];
    labels.forEach((key) => {
      if (obj1[key] !== obj2[key]) {
        diffList.push(key);
      }
    });
    return diffList;
  };

  // Build the layout
  const initData = strfobj(currDate);
  labels.forEach((label) => {
    element.insertAdjacentHTML('beforeend', `
      <div class="time ${label}">
        <span class="count curr top">${initData[label]}</span>
        <span class="count next top">${initData[label]}</span>
        <span class="count next bottom">${initData[label]}</span>
        <span class="count curr bottom">${initData[label]}</span>
        <span class="label">${label}</span>
      </div>`);
  });

  // Starts the countdown
  window.$(element).countdown(timezoneAwareDate, (event) => {
    const newDate = event.strftime('%D %H %M %S');
    let data;
    if (newDate !== nextDate) {
      currDate = nextDate;
      nextDate = newDate;
      // Setup the data
      data = {
        curr: strfobj(currDate),
        next: strfobj(nextDate)
      };
      // Apply the new values to each node that changed
      diff(data.curr, data.next).forEach((label) => {
        const selector = '.%s'.replace(/%s/, label);
        const node = element.querySelector(selector);
        // Update the node
        node.classList.remove('flip');
        node.querySelectorAll('.curr').forEach((innerElement) => {
          // eslint-disable-next-line no-param-reassign
          innerElement.innerHTML = data.curr[label];
        });
        node.querySelectorAll('.next').forEach((innerElement) => {
          // eslint-disable-next-line no-param-reassign
          innerElement.innerHTML = data.next[label];
        });
        // Wait for a repaint to then flip
        setTimeout(() => {
          node.classList.add('flip');
        }, 50);
      });
    }
  });
};

const FlipTimer = ({ className, endTime, backgroundColor }) => {
  const ref = createRef();
  useEffect(() => {
    initFlipCountdown('AU', ref.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={ref}
      className={classnames('flip-timer', className)}
      data-flip-countdown={endTime}
      style={{ backgroundColor }}
    />
  );
};

FlipTimer.propTypes = {
  className: PropTypes.string,
  endTime: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string
};

FlipTimer.defaultTypes = {
  className: null,
  backgroundColor: '#ffffff',
};

export { FlipTimer };
